import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { IThresholdsApiResponse } from './transactionThresholdsTypes';

interface IToggleGlobalThresholdInput {
  isActive: boolean;
}

const initialState: IApiState<IThresholdsApiResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const toggleGlobalThresholdAsync = createAsyncThunk(
  'business-settings/2fa-thresholds/global/toggle',
  async (
    { isActive }: IToggleGlobalThresholdInput,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const { data } = await api.patch<IThresholdsApiResponse>(
        '/business-settings/2fa-thresholds/global',
        { isActive },
        {
          headers: {
            Authorization: `Token ${accessToken}`,
            'X-Business-Id': businessId,
          },
        },
      );

      dispatch(
        showAlert({
          status: 'success',
          description:
            data?.message ?? 'Global 2FA threshold has been updated',
        }),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IThresholdsApiResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(showAlert({ status: 'error', description: errorMessage }));

      return rejectWithValue(error.response?.data);
    }
  },
);

export const toggleGlobalThresholdSlice = createSlice({
  name: 'business-settings/2fa-thresholds/global/toggle',
  initialState,
  reducers: {
    resetToggleGlobalThreshold: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(toggleGlobalThresholdAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(toggleGlobalThresholdAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(toggleGlobalThresholdAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as IThresholdsApiResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetToggleGlobalThreshold } =
  toggleGlobalThresholdSlice.actions;

export default toggleGlobalThresholdSlice.reducer;
